import { useAuth } from '@context/AuthContext';
import type { TagInfo } from '@interfaces/general';
import axiosInstance from '@services/axiosInstance';
import { isTransientAuthColdStart } from '@services/authReady';
import { createCache, deleteCacheItem, getCacheData, updateCacheItem } from '@services/cache';
import publicInstance from '@services/publicInstance';
import { AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstanceAPI from './axiosInstanceAPI';
import * as Sentry from '@sentry/react';

export const uploadImage = async (value: string) => {
  const parts = value.replace('data:image/', '').replace(';base64', '').split(',');

  if (parts.length === 2) {
    try {
      const response_image = await publicInstance.post('/v1/subir/imagen', {
        extension: parts[0],
        imagen: parts[1],
      });

      return response_image.data.imagen;
    } catch {
      // empty
    }
  }

  return null;
};

export const getTags = async () => {
  const response = await axiosInstance.get('/v1/tag');
  const result: Array<TagInfo> = response.data;
  return result;
};

const PENDING_REQUESTS = new Set();

export const useFetch = <K>(
  url: string,
  { path, params }: { path?: string; params?: Record<string, unknown> } = {},
) => {
  const auth = useAuth();
  const [isFetching, setFetching] = useState(true);
  const [response, setResponse] = useState<K>();
  const [error, setError] = useState<Error>();
  const requestUrl = `${url}${path ? `/${path}` : ''}`;

  const cachekey = requestUrl + JSON.stringify(params || {});
  const cacheData = getCacheData<K>(cachekey);

  useEffect(() => {
    if (PENDING_REQUESTS.has(cachekey)) return;
    (async () => {
      try {
        if (!cacheData) {
          PENDING_REQUESTS.add(cachekey);
          setFetching(true);
          const { updateCache } = createCache<K>(cachekey);
          const resp = await (url.endsWith('timezone') ? publicInstance : axiosInstance).get<K>(requestUrl, { params });
          if (resp.status >= 400) {
            if ((resp.status === 401 || resp.status === 403) && !auth.isDeliveryPerson) auth.logout();
            else setError(new Error(`Get Error with status ${resp.status} to url: ${url}`));
          } else {
            updateCache(resp.data);
            setResponse(resp.data);
          }
        } else {
          setResponse(cacheData);
        }
      } catch (err) {
        // Cold-start: fallo transitorio de arranque (request sin token, reintentos agotados). No
        // fijar `error` (evita el estado de error y el toast del consumidor); al restaurarse la
        // sesión el interceptor reintenta y el refetch trae la data. No tirar para no romper el flujo.
        if (isTransientAuthColdStart(err)) {
          return;
        }
        if (err instanceof Error) {
          setError(err);
        }
        throw new Error(`Error in request url: ${url}`);
      } finally {
        setFetching(false);
        PENDING_REQUESTS.delete(cachekey);
      }
    })();
  }, [cachekey, cacheData]);
  return { isFetching, response, error };
};

const PENDING_QUERIES = new Map<string, Promise<unknown>>();
type Version = 'v1' | 'v2';
interface FetchCallbackOptions {
  /**
   * Handler de 401 propio del caller. Se usa en apps que NO montan el `AuthProvider` del panel
   * (p. ej. la app del partner en PRD-037): ahi `auth.logout()` resuelve al no-op del contexto por
   * defecto — no limpia el token expirado de localStorage ni cierra sesion. Cuando se provee, el 401
   * invoca ESTE handler en lugar de `auth.logout()`, para que el caller limpie su propio token-draft.
   * En el panel (sin este option) el comportamiento no cambia: sigue llamando `auth.logout()`.
   */
  onUnauthorized?: () => void;
}

export const useFetchCallback = <TData, TResponse = TData>(
  method: 'get' | 'post' | 'delete' | 'put' | 'patch',
  url: string,
  version: Version = 'v1',
  options: FetchCallbackOptions = {},
) => {
  const auth = useAuth();
  const [isFetching, setFetching] = useState(false);
  // `call` es un useCallback con deps `[]` (identidad estable): captura el closure del primer render.
  // Guardamos `onUnauthorized` en un ref actualizado en cada render para que el 401 use SIEMPRE el
  // handler vigente sin re-crear `call` (que dispararia refetches en los consumidores).
  const onUnauthorizedRef = useRef(options.onUnauthorized);
  onUnauthorizedRef.current = options.onUnauthorized;

  const call = useCallback<
    (queryData?: { path?: string; data?: TData & AxiosRequestConfig<TData> }, token?: string) => Promise<TResponse>
  >(async ({ path, data } = {}, token) => {
    const requestKey = `${method}:${url}${path ? `/${path}` : ''}${data ? `/${JSON.stringify(data)}` : ''}`;

    setFetching(true);
    try {
      const queryPromise =
        (PENDING_QUERIES.get(requestKey) as Promise<AxiosResponse<TResponse>>) ??
        (url.includes('/public/') ? publicInstance : version === 'v2' ? axiosInstanceAPI : axiosInstance)[method](
          `${url}${path ? `/${path}` : ''}`,
          (method === 'get' || method === 'delete') && token
            ? ({
                headers: { Authorization: token && `Bearer ${token}` },
              } as unknown as undefined)
            : data,
          token
            ? {
                headers: { Authorization: `Bearer ${token}` },
              }
            : undefined,
        );
      if (!PENDING_QUERIES.has(requestKey)) PENDING_QUERIES.set(requestKey, queryPromise);

      const resp = await queryPromise;
      return resp.data;
    } catch (err) {
      // Cold-start: el interceptor de respuesta agotó sus reintentos sin token (la request se
      // coló antes de que la sesión se restaurara). NO es una sesión expirada — no hacer logout
      // (cerraría una sesión válida por una carrera de arranque). Propagar etiquetado para que el
      // caller suprima el toast; al llegar el token la data carga sola (interceptor + refetch).
      if (isTransientAuthColdStart(err)) {
        throw err;
      }
      if (isAxiosError(err)) {
        const status = err.response?.status ?? 0;
        if (status === 401 && !auth.isDeliveryPerson) {
          // Caller con handler propio (app sin AuthProvider del panel) → lo usa para limpiar SU
          // token; si no, se conserva el logout del panel. En ambos casos se propaga "sesion
          // expirada" para que el consumidor muestre el mensaje correcto (no uno generico).
          const onUnauthorized = onUnauthorizedRef.current;
          if (onUnauthorized) onUnauthorized();
          else auth.logout();
          throw new Error('Sesion expirada, es necesario volver a iniciar sesión');
        } else if (status >= 400) {
          // Captura centralizada de errores HTTP de la capa de red. Excluye el 401 ya
          // manejado arriba (sesión expirada → logout, ruido esperado). `url` es la clave
          // estática del endpoint (sin path dinámico → sin PII). `errorCode` y `response.data`
          // permiten triar el contrato de error del backend sin reproducir.
          try {
            const errorCode = err.response?.data?.errorCode;
            Sentry.withScope((scope) => {
              scope.setTag('http_status', String(status));
              scope.setTag('method', method);
              scope.setTag('url_key', url);
              if (errorCode) scope.setTag('errorCode', String(errorCode));
              scope.setFingerprint(['http', method, url, String(status)]);
              if (err.response?.data) scope.setExtra('response_data', err.response.data);
              Sentry.captureException(err);
            });
          } catch {
            // off — la observabilidad no debe romper la app
          }
          // Preservar el error original de Axios para mantener response.data
          throw err;
        }
      }
      if (err instanceof Error) {
        // empty
      }
      throw err;
    } finally {
      setFetching(false);
      PENDING_QUERIES.delete(requestKey);
    }
  }, []);

  return { isFetching, call };
};

export const useEditItem = <K extends { id: string }>(url: string, cacheKey?: string) => {
  const [updatingIds, setIds] = useState<string[]>([]);
  const { call: callUpdate } = useFetchCallback<K, K>('put', url);
  const handleUpdate = async (item: K) => {
    if (item.id) {
      setIds((current) => [...current, item.id]);
      try {
        await callUpdate({ data: item, path: item.id });
        if (cacheKey) updateCacheItem<K>(cacheKey, { id: item.id, data: item });
      } finally {
        setIds((current) => {
          current.splice(current.indexOf(item.id), 1);
          return [...current];
        });
      }
    }
  };
  return { updatingIds, handleUpdate };
};

export const useDeleteItem = <K extends { id: string }>(url: string, cacheKey?: string) => {
  const [deletingIds, setIds] = useState<string[]>([]);
  const { call: callDelete } = useFetchCallback('delete', url);
  const handleDelete = async (itemId: string) => {
    if (itemId) {
      setIds((current) => [...current, itemId]);
      try {
        await callDelete({ path: itemId });
        if (cacheKey) deleteCacheItem<K>(cacheKey, itemId);
      } finally {
        setIds((current) => {
          current.splice(current.indexOf(itemId), 1);
          return [...current];
        });
      }
    }
  };
  return { deletingIds, handleDelete };
};

export const subscribeToPushNotifications = async (token: string, commerceId: string) => {
  const response = await axiosInstance.post('/v1/notificacion-push-suscribe', {
    token,
    commerceId,
    action: 'subscribe',
  });
  return response.data;
};

export const unsubscribeFromPushNotifications = async (token: string, commerceId: string) => {
  const response = await axiosInstance.post('/v1/notificacion-push-suscribe', {
    token,
    commerceId,
    action: 'subscribe',
  });
  return response.data;
};
